import { useEffect, useState, useCallback } from "react";
import { api, formatDate, formatApiError } from "@/lib/api";
import { usePermissions } from "@/contexts/PermissionsContext";
import { PageHeader } from "@/components/shared/Common";
import { DateRangePicker } from "@/components/shared/DateRangePicker";
import { Pagination } from "@/components/shared/Pagination";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { LockKey, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";

const MODULE_LABELS = {
  dashboard: "Dashboard",
  my_queue: "My Queue",
  queue_board: "Queue Board",
  cases: "Cases",
  new_case: "Create Case",
  allocation: "Allocation Engine",
  workforce: "Workforce",
  exceptions: "Exceptions",
  analytics: "Analytics",
  reports: "Reports",
  notifications: "Notifications",
  admin: "Administration",
};

const ROLES = [
  { key: "associate", label: "Associate", desc: "Individual contributors" },
  { key: "manager", label: "Manager", desc: "Operational owners" },
  { key: "leadership", label: "Leadership", desc: "Executive read-only" },
];

export default function Admin() {
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [skills, setSkills] = useState([]);
  const [audit, setAudit] = useState([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditDateRange, setAuditDateRange] = useState(null);
  const [auditPage, setAuditPage] = useState(1);
  const [auditPageSize, setAuditPageSize] = useState(25);
  const [newTeam, setNewTeam] = useState({ name: "", description: "" });
  const [newSkill, setNewSkill] = useState({ name: "", category: "General" });
  const [permData, setPermData] = useState(null);
  const [savingPerms, setSavingPerms] = useState(false);
  const { refresh: refreshPerms } = usePermissions();

  const loadStatic = useCallback(async () => {
    const [u, t, s, p] = await Promise.all([
      api.get("/users"),
      api.get("/teams"),
      api.get("/skills"),
      api.get("/admin/permissions"),
    ]);
    setUsers(u.data.items);
    setTeams(t.data.items);
    setSkills(s.data.items);
    setPermData({ modules: p.data.modules, permissions: p.data.permissions });
  }, []);

  const loadAudit = useCallback(async () => {
    const q = new URLSearchParams();
    if (auditDateRange?.from) q.set("date_from", auditDateRange.from);
    if (auditDateRange?.to) q.set("date_to", auditDateRange.to);
    q.set("limit", String(auditPageSize));
    q.set("skip", String((auditPage - 1) * auditPageSize));
    const { data } = await api.get(`/audit-logs?${q.toString()}`);
    setAudit(data.items);
    setAuditTotal(data.total || data.items.length);
  }, [auditDateRange, auditPage, auditPageSize]);

  useEffect(() => { loadStatic(); }, [loadStatic]);
  useEffect(() => { loadAudit(); }, [loadAudit]);
  useEffect(() => { setAuditPage(1); }, [auditDateRange, auditPageSize]);

  const createTeam = async () => {
    if (!newTeam.name) return;
    try { await api.post("/teams", newTeam); toast.success("Team created"); setNewTeam({ name: "", description: "" }); loadStatic(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const createSkill = async () => {
    if (!newSkill.name) return;
    try { await api.post("/skills", newSkill); toast.success("Skill created"); setNewSkill({ name: "", category: "General" }); loadStatic(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const togglePerm = (role, mod) => {
    if (!permData) return;
    setPermData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [role]: { ...prev.permissions[role], [mod]: !prev.permissions[role][mod] }
      }
    }));
  };

  const savePermissions = async () => {
    if (!permData) return;
    setSavingPerms(true);
    try {
      await api.put("/admin/permissions", { permissions: permData.permissions });
      toast.success("Permissions saved");
      await refreshPerms();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingPerms(false); }
  };

  return (
    <div>
      <PageHeader title="Administration" subtitle="Users, teams, skills, permissions & audit logs" />

      <Tabs defaultValue="users" className="w-full">
        <TabsList className="h-auto flex-wrap">
          <TabsTrigger value="users" data-testid="admin-tab-users">Users</TabsTrigger>
          <TabsTrigger value="teams" data-testid="admin-tab-teams">Teams</TabsTrigger>
          <TabsTrigger value="skills" data-testid="admin-tab-skills">Skills</TabsTrigger>
          <TabsTrigger value="permissions" data-testid="admin-tab-permissions">
            <ShieldCheck size={14} className="mr-1" /> Permissions
          </TabsTrigger>
          <TabsTrigger value="audit" data-testid="admin-tab-audit">Audit logs</TabsTrigger>
        </TabsList>

        <TabsContent value="users">
          <div className="border rounded-md bg-card">
            <table className="w-full text-sm">
              <thead className="text-left text-[11px] uppercase tracking-widest text-muted-foreground border-b">
                <tr>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3">Joined</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {users.map(u => (
                  <tr key={u.id}>
                    <td className="px-4 py-3 font-medium">{u.name}</td>
                    <td className="px-4 py-3 mono text-xs">{u.email}</td>
                    <td className="px-4 py-3"><span className="text-[10px] uppercase tracking-widest bg-secondary rounded px-2 py-0.5">{u.role}</span></td>
                    <td className="px-4 py-3 text-xs mono text-muted-foreground">{u.created_at?.slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="teams">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 border rounded-md bg-card divide-y">
              {teams.map(t => (
                <div key={t.id} className="p-4">
                  <div className="font-semibold">{t.name}</div>
                  <div className="text-xs text-muted-foreground">{t.description}</div>
                </div>
              ))}
            </div>
            <div className="border rounded-md bg-card p-5 h-fit">
              <div className="eyebrow text-muted-foreground mb-3">Create team</div>
              <div className="space-y-3">
                <div>
                  <Label>Name</Label>
                  <Input data-testid="new-team-name" value={newTeam.name} onChange={e => setNewTeam(t => ({ ...t, name: e.target.value }))} className="mt-1" />
                </div>
                <div>
                  <Label>Description</Label>
                  <Input value={newTeam.description} onChange={e => setNewTeam(t => ({ ...t, description: e.target.value }))} className="mt-1" />
                </div>
                <Button data-testid="create-team-btn" onClick={createTeam}>Create team</Button>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="skills">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 border rounded-md bg-card p-5">
              <div className="flex flex-wrap gap-2">
                {skills.map(s => (
                  <span key={s.id} className="text-sm bg-secondary px-3 py-1 rounded-md">{s.name}</span>
                ))}
              </div>
            </div>
            <div className="border rounded-md bg-card p-5 h-fit">
              <div className="eyebrow text-muted-foreground mb-3">Add skill</div>
              <div className="space-y-3">
                <Input data-testid="new-skill-name" value={newSkill.name} onChange={e => setNewSkill(s => ({ ...s, name: e.target.value }))} placeholder="Skill name" />
                <Button data-testid="create-skill-btn" onClick={createSkill}>Add skill</Button>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="permissions">
          <div className="border rounded-md bg-card p-5">
            <div className="flex items-start justify-between mb-4 gap-4 flex-wrap">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-md bg-primary/10 text-primary grid place-items-center shrink-0">
                  <LockKey size={20} weight="fill" />
                </div>
                <div>
                  <div className="font-semibold">Role & module permissions</div>
                  <div className="text-xs text-muted-foreground max-w-lg">
                    Control which modules each role can access. Changes take effect immediately in the sidebar for signed-in users after a page refresh.
                    Manager access to Administration and Dashboard cannot be disabled to prevent lock-out.
                  </div>
                </div>
              </div>
              <Button data-testid="save-permissions-btn" onClick={savePermissions} disabled={savingPerms || !permData}>
                {savingPerms ? "Saving…" : "Save permissions"}
              </Button>
            </div>

            {permData && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm border rounded">
                  <thead className="text-left text-[11px] uppercase tracking-widest text-muted-foreground border-b bg-secondary/40">
                    <tr>
                      <th className="px-4 py-3 min-w-[220px]">Module</th>
                      {ROLES.map(r => (
                        <th key={r.key} className="px-4 py-3 text-center min-w-[140px]">
                          <div>{r.label}</div>
                          <div className="text-[10px] font-normal text-muted-foreground tracking-normal normal-case">{r.desc}</div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {permData.modules.map(m => (
                      <tr key={m} data-testid={`perm-row-${m}`}>
                        <td className="px-4 py-3">
                          <div className="font-medium">{MODULE_LABELS[m] || m}</div>
                          <div className="text-[10px] mono text-muted-foreground">{m}</div>
                        </td>
                        {ROLES.map(r => {
                          const locked = r.key === "manager" && (m === "admin" || m === "dashboard");
                          const enabled = permData.permissions?.[r.key]?.[m];
                          return (
                            <td key={r.key} className="px-4 py-3 text-center">
                              <div className="flex justify-center">
                                <Switch
                                  data-testid={`perm-${r.key}-${m}`}
                                  checked={!!enabled}
                                  disabled={locked}
                                  onCheckedChange={() => togglePerm(r.key, m)}
                                />
                              </div>
                              {locked && <div className="text-[9px] mono text-muted-foreground mt-1">Required</div>}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="audit">
          <div className="border rounded-md bg-card">
            <div className="p-4 border-b flex justify-end">
              <DateRangePicker testId="audit-date-range" value={auditDateRange} onChange={setAuditDateRange} />
            </div>
            <table className="w-full text-sm">
              <thead className="text-left text-[11px] uppercase tracking-widest text-muted-foreground border-b">
                <tr>
                  <th className="px-4 py-3">Action</th>
                  <th className="px-4 py-3">Entity</th>
                  <th className="px-4 py-3">Actor</th>
                  <th className="px-4 py-3">At</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {audit.map(a => (
                  <tr key={a.id}>
                    <td className="px-4 py-3 mono text-xs">{a.action}</td>
                    <td className="px-4 py-3 text-muted-foreground">{a.entity}</td>
                    <td className="px-4 py-3 mono text-xs">{a.actor_id?.slice(0, 8)}…</td>
                    <td className="px-4 py-3 text-xs mono text-muted-foreground">{formatDate(a.at)}</td>
                  </tr>
                ))}
                {audit.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-sm text-muted-foreground">No audit logs</td></tr>}
              </tbody>
            </table>
            <Pagination testId="audit-pagination" page={auditPage} pageSize={auditPageSize} total={auditTotal} onPageChange={setAuditPage} onPageSizeChange={setAuditPageSize} />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
